import "./style.scss";
import React,{ useEffect, useRef, useState } from "react";
import { Menu, MenuItem, Sidebar } from "react-pro-sidebar";
import { collapseAtom } from "../../utils/atoms/collapseAtom";
import { tourAtom } from "../../utils/atoms/tourAtom";
import { useAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useElementSize } from "@mantine/hooks";
import { SideBarSkeleton } from "../Skeleton";
// Mantine imports consolidated below
import PropTypes from "prop-types";
import { useSidebarTourSticky } from "../../utils/tour/useSidebarTourSticky";
import { createPortal } from "react-dom";
import { IconCopy, IconTrash, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useAssessmentRouteParams, isHtmlTag, convertPictureToBlobUrl } from "../../constants/helper";
import SEConfirmModal from "../common/SEConfirmModal";
import MGAAPI from "../../api/index";
import {
  flattenCountComponentDTOList,
  flattenDesignCountComponent,
  projectDetailsAtom,
} from "../../utils/atoms/projectDetailsAtom";
import { loaderAtom } from "../../utils/atoms/loaderAtom";
import { activeTabAtom } from "../../utils/atoms/activeTabAtom";
import {
  useMantineColorScheme,
  Table,
  Loader,
  Group,
  Text,
  TextInput,
  Grid,
  Col,
  Stack,
  Badge,
  Avatar,
  ActionIcon,
  Textarea,
} from "@mantine/core";
import { userScopeAtom } from "../../utils/atoms/userScopeAtom";
import { componentStatusAtom } from "../../utils/atoms/componentStatus";
import { useTranslation, Trans } from "react-i18next";
import { useDualTranslation } from "../../utils/i18nHelpers";
import displayError from "../../constants/utils";
import { getErrorMessage } from "../../utils/errorUtils";

const MGA_API = new MGAAPI();

// Reusable form fields for create/clone version modals
const VersionFormFields = ({
  heading,
  nameLabel,
  descLabel,
  nameValue,
  onNameChange,
  descValue,
  onDescChange,
  nameError,
  descError,
}) => (
  <Stack spacing="sm" sx={{ minWidth: "260px" }}>
    {heading}
    <TextInput
      label={nameLabel}
      value={nameValue}
      onChange={onNameChange}
      required
      error={nameError || undefined}
    />
    <Textarea
      label={descLabel}
      minRows={3}
      value={descValue}
      onChange={onDescChange}
      required
      error={descError || undefined}
    />
  </Stack>
);

// Small helper hook to manage paired name/description values + errors
const useVersionForm = (initialName = "", initialDesc = "") => {
  const [values, setValues] = useState({ name: initialName, desc: initialDesc });
  const [errors, setErrors] = useState({ name: "", desc: "" });

  const setName = (name) => {
    setValues((prev) => ({ ...prev, name }));
    setErrors((prev) => ({ ...prev, name: "" }));
  };
  const setDesc = (desc) => {
    setValues((prev) => ({ ...prev, desc }));
    setErrors((prev) => ({ ...prev, desc: "" }));
  };
  const reset = (name = "", desc = "") => {
    setValues({ name, desc });
    setErrors({ name: "", desc: "" });
  };

  return { values, errors, setName, setDesc, setErrors, reset };
};

// Format updatedOn values to a readable string while staying resilient to bad data
const formatUpdatedOn = (raw) => {
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(raw);
  const datePart = date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
  const timePart = date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return `${datePart} ${timePart}`;
};

export default function SideMenu(props) {
  const { t } = useDualTranslation();
  const [loader, setLoader] = useAtom(loaderAtom);
  const [showDeleteVersionModal, setShowDeleteVersionModal] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState(null);

  const [collapseSideMenu, setCollapseSideMenu] = useAtom(collapseAtom);
  const [openMenuVersion, setOpenMenuVersion] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  const [tour] = useAtom(tourAtom);
  const toggleRef = useRef(null);
  const contextMenuIconRefs = useRef({});
  const contextMenuPortalRef = useRef(null);
  const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const [activeTab, setActiveTab] = useState(designVersionId);
  const navigate = useNavigate();
  const { ref } = useElementSize();
  const { colorScheme } = useMantineColorScheme();
  const [projectDetails, setProjectDetails] = useAtom(projectDetailsAtom);
  const [userScope] = useAtom(userScopeAtom);
  const [showClonedVersionModal, setShowClonedVersionModal] = useState(false);
  const [clonedVersionName, setClonedVersionName] = useState("");
  const [activeState, setActiveState] = useAtom(activeTabAtom);
  const [collabModalOpen, setCollabModalOpen] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [collabLoading, setCollabLoading] = useState(false);
  const [collabError, setCollabError] = useState("");
  const [showVersionDetailsModal, setShowVersionDetailsModal] = useState(false);
  const [versionDetailsData, setVersionDetailsData] = useState(null);
  const [versionDetailsDesignId, setVersionDetailsDesignId] = useState(null);
  const [versionDetailsName, setVersionDetailsName] = useState("");
  const [versionDetailsDesc, setVersionDetailsDesc] = useState("");
  const [versionDetailsNameError, setVersionDetailsNameError] = useState("");
  const [versionDetailsDescError, setVersionDetailsDescError] = useState("");
  const [sharedUserProfileModalOpen, setSharedUserProfileModalOpen] =
    useState(false);
  const [sharedUserProfileLoading, setSharedUserProfileLoading] =
    useState(false);
  const [sharedUserProfileError, setSharedUserProfileError] = useState("");
  const [sharedUserProfileData, setSharedUserProfileData] = useState(null);
  const [componentStatus] = useAtom(componentStatusAtom);
  const resetComponentStatus = useResetAtom(componentStatusAtom);
  const [showVersionSwitchBlockedModal, setShowVersionSwitchBlockedModal] = useState(false);
  const [blockedComponentName, setBlockedComponentName] = useState("");
  const [pendingVersionId, setPendingVersionId] = useState(null);
  const sidebarRef = useRef(null);

  const applyProjectRefresh = (editRes) => {
    setProjectDetails((prev) => ({
      ...prev,
      userId: editRes.userId || "",
      currencyCode: editRes?.projectDTO?.currencyCode || "$",
      projectDTO: editRes.projectDTO || {},
      componentDTOList: flattenCountComponentDTOList(editRes.countComponentDTOList) || [],
      designWithVersionsDTOList: editRes.designWithVersionsDTOList || [],
      locationDTO: editRes.locationDTO || {},
      countComponentDTOList: editRes.countComponentDTOList,
      designComponentsList: mergeDesignComponentsList(
        prev?.designComponentsList,
        editRes.designComponentsList
      ),
    }));
    return editRes?.designWithVersionsDTOList || [];
  };

  const validateVersionInputs = ({ name, desc, versions, excludeId }) => {
    const trimmedName = (name || "").trim();
    const trimmedDesc = (desc || "").trim();
    const hasHtml = (value) => isHtmlTag(value);
    let nameError = "";
    let descError = "";

    if (!trimmedName) nameError = t("sideMenu.validation.nameRequired");
    if (!nameError && hasHtml(trimmedName)) nameError = "HTML tags are not allowed";
    if (!nameError && trimmedName.length > 15)
      nameError = t("sideMenu.validation.nameMax");

    if (!trimmedDesc) descError = t("sideMenu.validation.descRequired");
    if (!descError && hasHtml(trimmedDesc)) descError = "HTML tags are not allowed";
    if (!descError && trimmedDesc.length > 60)
      descError = t("sideMenu.validation.descMax");

    if (!nameError && Array.isArray(versions)) {
      const normalizedName = trimmedName.toLowerCase();
      const isDuplicate = versions.some((v) => {
        if (excludeId && String(v?.designVersionId) === String(excludeId)) {
          return false;
        }
        const normalizedExisting = (v?.version || "").trim().toLowerCase();
        return normalizedExisting === normalizedName;
      });
      if (isDuplicate) {
        nameError = t("sideMenu.validation.nameUnique");
      }
    }

    return { trimmedName, trimmedDesc, nameError, descError };
  };

  const runVersionMutation = async ({
    action,
    name,
    desc,
    sourceVersionId,
  }) => {
    const payload = {
      version: name,
      versionDescription: desc,
      isUpdate: false,
    };
    const res =
      action === "clone"
        ? await MGA_API.cloneVersion(projectId, designId, sourceVersionId, payload)
        : await MGA_API.createDesignVersion(projectId, designId, -1, payload);

    const editRes = await MGA_API.editProject(projectId);
    const designList = applyProjectRefresh(editRes);
    return { res, editRes, designList };
  };

  const handleSharedUserClick = (email) => {
    if (!email || sharedUserProfileLoading) return;
    setSharedUserProfileModalOpen(true);
    setSharedUserProfileLoading(true);
    setSharedUserProfileError("");
    MGA_API.getUserProfileInfo(email)
      .then((res) => {
        setSharedUserProfileData(res || {});
      })
      .catch((e) => {
        setSharedUserProfileError(e?.message || t("sideMenu.failedLoadUser"));
        setSharedUserProfileData(null);
        displayError('error', getErrorMessage(e), "Failed to load user profile");
      })
      .finally(() => {
        setSharedUserProfileLoading(false);
      });
  };

  // Collapse helper to close the hovered sidemenu after modal actions
  const collapseLeftMenu = () => {
    // setCollapseSideMenu((prev) => ({ ...prev, leftMenu: true }));
  };

  const openCollaborators = async () => {
    if (!projectId) return;
    setCollabModalOpen(true);
    setCollabLoading(true);
    setCollabError("");
    try {
      const res = await MGA_API.getSharedProject(projectId);
      // Assume API returns array like [{email:"a@b.com"}, ...] or simple list of emails
      let list = [];
      if (Array.isArray(res)) {
        if (res.length && typeof res[0] === "string") {
          list = res.map((e) => ({ email: e }));
        } else {
          list = res.map((r) => ({ email: r.email || r.userEmail || r }));
        }
      }
      setCollaborators(list.filter((c) => !!c.email));
    } catch (e) {
      setCollabError(e?.message || t("sideMenu.failedLoadCollaborators"));
      setCollaborators([]);
      displayError('error', getErrorMessage(e), "Failed to load collaborators");
    } finally {
      setCollabLoading(false);
      // Ensure sidebar collapses after data loads (modal interaction complete)
      collapseLeftMenu();
    }
  };

  const navigateToVersion = (version) => {
    resetComponentStatus();
    setActiveTab(version.toString());
    if (projectId && designId && version) {
      setActiveState("DESIGN");
      navigate(`/assessment/${projectId}/${designId}/${version}?tab=design`);
    }
  };

  const handleMenuItemClick = (version) => {
    if (activeTab === version?.toString()) return;

    const editingComponent =
      activeState === "DESIGN"
        ? Object.entries(componentStatus || {}).find(
            ([, status]) =>
              status &&
              typeof status === "object" &&
              status?.isEditing &&
              !status?.completed
          )
        : null;

    if (editingComponent) {
      const componentKey = editingComponent?.[0] || "";
      setBlockedComponentName(
        componentKey === "battery" ? "BESS" : componentKey === "controller" ? "Controls" : componentKey
      );
      setPendingVersionId(version?.toString());
      setShowVersionSwitchBlockedModal(true);
      return;
    }

    navigateToVersion(version);
  };

  // Merge previous designComponentsList with new API response
  // Only add new design versions/designs from API; keep existing versions unchanged
  const mergeDesignComponentsList = (prevList, apiResponse) => {
    const newList = flattenDesignCountComponent(apiResponse) || [];
    if (!prevList || prevList.length === 0) {
      return newList;
    }

    // Update existing designs by appending any new design versions from API
    const mergedDesigns = prevList.map((prevDesign) => {
      const matchingNewDesign = newList.find(
        (design) => String(design.designId) === String(prevDesign.designId)
      );

      if (!matchingNewDesign) {
        return prevDesign;
      }

      const prevVersions = prevDesign.designVersionComponents || [];
      const newVersions = matchingNewDesign.designVersionComponents || [];

      // Add only design versions that do not already exist in prevVersions
      const versionsToAdd = newVersions.filter(
        (newVersion) =>
          !prevVersions.some(
            (prevVersion) =>
              String(prevVersion.designVersionId) === String(newVersion.designVersionId)
          )
      );

      return {
        ...prevDesign,
        designVersionComponents: [...prevVersions, ...versionsToAdd],
      };
    });

    // Add any entirely new designs from the API response
    const newDesigns = newList.filter(
      (newDesign) =>
        !prevList.some(
          (prevDesign) => String(prevDesign.designId) === String(newDesign.designId)
        )
    );

    return [...mergedDesigns, ...newDesigns];
  };

  const handleDeleteVersion = async (versionObj) => {
    if (!projectId || !designId || !versionObj?.designVersionId) return;
    const design = projectDetails?.designWithVersionsDTOList?.find(
      (d) => d?.designDTO?.designId === Number(designId)
    );
    const versionsArr = design?.designVersionDTO || [];
    if (versionsArr?.length < 2) {
      displayError('error', t("sideMenu.versionDetails.deleteLastVersion"), "Failed to delete version");
      return;
    }
    setLoader(true);
    try {
      await MGA_API.deleteVersion(
        projectId,
        designId,
        versionObj?.designVersionId
      );
      const editRes = await MGA_API.editProject(projectId);
      setProjectDetails((prev) => ({
        ...prev,
        userId: editRes.userId || "",
        currencyCode: editRes?.projectDTO?.currencyCode || "$",
        projectDTO: editRes.projectDTO || {},
        componentDTOList:
          flattenCountComponentDTOList(editRes.countComponentDTOList) || [],
        designWithVersionsDTOList: editRes.designWithVersionsDTOList || [],
        locationDTO: editRes.locationDTO || {},
        countComponentDTOList: editRes.countComponentDTOList,
        designComponentsList: mergeDesignComponentsList(prev?.designComponentsList, editRes.designComponentsList),
      }));
      const updatedDesign = editRes?.designWithVersionsDTOList?.find(
        (d) => d?.designDTO?.designId === Number(designId)
      );
      const updatedVersionsArr = updatedDesign?.designVersionDTO || [];
      if (updatedVersionsArr?.length > 0) {
        const lastVersion = updatedVersionsArr[updatedVersionsArr?.length - 1];
        setActiveTab(lastVersion?.designVersionId?.toString());
        navigate(
          `/assessment/${projectId}/${designId}/${lastVersion?.designVersionId}?tab=design`
        );
      }
      setOpenMenuVersion(null);
      displayError('success', t('sideMenu.versionDeleted', { version: versionObj?.version }));
    } catch (err) {
      displayError('error', getErrorMessage(err));
    } finally {
      setLoader(false);
    }
  };
  useEffect(() => {
    if (!openMenuVersion) return;
    const handleClick = (e) => {
      // If click is inside any context menu icon, ignore
      const iconEl = contextMenuIconRefs.current[openMenuVersion];
      if (iconEl && iconEl.contains(e.target)) return;
      // If click is inside the portal menu itself, ignore
      if (contextMenuPortalRef.current && contextMenuPortalRef.current.contains(e.target)) return;
      // If click is inside any menu-option, ignore
      if (e.target.classList && e.target.classList.contains("menu-option"))
        return;
      setOpenMenuVersion(null);
    };
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [openMenuVersion]);

  useEffect(() => {
    if (showVersionDetailsModal && versionDetailsData) {
      setVersionDetailsName(versionDetailsData?.version || "");
      setVersionDetailsDesc(
        versionDetailsData?.description ||
          versionDetailsData?.versionDescription ||
          ""
      );
      setVersionDetailsNameError("");
      setVersionDetailsDescError("");
    }
  }, [showVersionDetailsModal, versionDetailsData]);
  // Close context menu when sidebar is collapsed
  useEffect(() => {
    if (collapseSideMenu.leftMenu) {
      setOpenMenuVersion(null);
    }
  }, [collapseSideMenu.leftMenu]);

  // Removed SubMenu expand icon manipulation since we now flatten designs & versions
  useEffect(() => {}, [props.loading]);

  // Collapse when clicking outside the sidebar (avoids blur double-calls)


  // useSidebarTourSticky({
  //   toggleRef,
  //   tourActive: tour,
  //   highlightClass: 'tour-highlight',
  //   zIndexActive: 99999,
  //   zIndexInactive: 11,
  //   disableRootSticky: true,
  //   disableContainerSticky: true,
  // });

  const getMenuItemBackgroundColor = (version) => {
    // if (colorScheme === "dark") return "var(--se-background-alternative)";
    if (activeTab === version.toString())
      return "var(--se-background-standard)";
    return "inherit";
  };

  const getMenuItemBorderColor = (version) => {
    // if (colorScheme === "dark") return "none";
    if (activeTab === version.toString()) return "1px solid #3dcd58";
    return "inherit";
  };

  const [showAddVersionModal, setShowAddVersionModal] = useState(false);
  const [showMaxVersionModal, setShowMaxVersionModal] = useState(false);
  const [modalDesignName, setModalDesignName] = useState("");
  const [modalProjectName, setModalProjectName] = useState("");
  const createForm = useVersionForm();
  const cloneForm = useVersionForm();
  const [showDuplicateVersionModal, setShowDuplicateVersionModal] =
    useState(false);
  const [versionToDuplicate, setVersionToDuplicate] = useState(null);
  // Success modal after creating a version
  const [showCreatedVersionModal, setShowCreatedVersionModal] = useState(false);
  const [createdVersionName, setCreatedVersionName] = useState("");
  useEffect(() => {
    if (projectDetails && projectDetails._pendingNavigateVersionId) {
      navigate(
        `/assessment/${projectId}/${designId}/${projectDetails._pendingNavigateVersionId}?tab=design`
      );
      setProjectDetails((prev) => {
        const { _pendingNavigateVersionId, ...rest } = prev;
        return rest;
      });
    }
  }, [projectDetails, projectId, designId, navigate, setProjectDetails]);
  // Hover intent timer to prevent instant open on brief hover
  const hoverTimerRef = useRef(null);
  const handleHoverEnter = () => {
    // Start a short timer; only expand if mouse stays within sidebar
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setCollapseSideMenu((prev) => ({ ...prev, leftMenu: false }));
    }, 200);
  };
  const handleHoverLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    // Do not collapse if a portal/context menu is open or user is interacting with overlays
    const portalOpen = !!(contextMenuPortalRef.current && document.body.contains(contextMenuPortalRef.current));
    const overlayOpen =
      collabModalOpen ||
      showAddVersionModal ||
      showMaxVersionModal ||
      showDeleteVersionModal ||
      showDuplicateVersionModal ||
      showCreatedVersionModal ||
      sharedUserProfileModalOpen ||
      portalOpen ||
      openMenuVersion;
    if (overlayOpen) return;
    setCollapseSideMenu((prev) => ({ ...prev, leftMenu: true }));
  };
    useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!sidebarRef.current) return;
      if (collapseSideMenu.leftMenu) return; // already collapsed
      // Clicking inside the context-menu dropdown (rendered in a portal) should NOT
      // be treated as an outside sidebar click.
      if (contextMenuPortalRef.current && contextMenuPortalRef.current.contains(e.target)) {
        return;
      }
      // If the portal is open (openMenuVersion) and the user clicks anywhere inside the portal,
      // do not collapse the sidebar. The earlier contains() check covers clicks inside the portal node,
      // but openMenuVersion indicates logical portal open state as well.
      if (openMenuVersion) {
        // If click target is within body but outside sidebar and outside portal, do not collapse yet
        // because user may be interacting with the portal.
        if (contextMenuPortalRef.current && contextMenuPortalRef.current.contains(e.target)) return;
      }
      const inside = sidebarRef.current.contains(e.target);
      // Ignore while overlays are open
      const overlayOpen =
        collabModalOpen ||
        showAddVersionModal ||
        showMaxVersionModal ||
        showDeleteVersionModal ||
        showDuplicateVersionModal ||
        showVersionDetailsModal ||
        showCreatedVersionModal ||
        sharedUserProfileModalOpen ||
        openMenuVersion ||
        (contextMenuPortalRef.current && document.body.contains(contextMenuPortalRef.current));
      if (overlayOpen) return;
      if (!inside) {
        setCollapseSideMenu((prev) => ({ ...prev, leftMenu: true }));
      }
    };
    document.addEventListener("mousedown", onDocMouseDown, true);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown, true);
    };
  }, [
    collapseSideMenu.leftMenu,
    collabModalOpen,
    showAddVersionModal,
    showMaxVersionModal,
    showDeleteVersionModal,
    showDuplicateVersionModal,
    showVersionDetailsModal,
    showCreatedVersionModal,
    sharedUserProfileModalOpen,
    setCollapseSideMenu,
  ]);
  return (
    <Sidebar
      className="sidebar sidebar-left"
      rootStyles={{
        top: 0,
        position: tour ? "relative" : "fixed",
        left: 0,
        zIndex: 11,
        height: "100vh",
        overflow: "visible",
        borderColor:
          colorScheme === "dark" ? "var(--se-accordian-standard)" : "#efefef",
      }}
      collapsed={collapseSideMenu.leftMenu}
      toggled={true}
      onMouseEnter={handleHoverEnter}
      onMouseLeave={handleHoverLeave}
      onClick={handleHoverEnter}
      ref={sidebarRef}
    >
      {/* Top-right circular toggle button */}
      <div
        className="sidebar-top-toggle"
        style={{
          position: "absolute",
          top: 8,
          right: -12, // half outside of sidebar
          zIndex: 100,
          pointerEvents: "auto",
        }}
      >
        <ActionIcon
          size={28}
          radius="xl"
          variant="filled"
          onClick={() =>
            setCollapseSideMenu((prev) => ({ ...prev, leftMenu: !prev.leftMenu }))
          }
          title={collapseSideMenu.leftMenu ? t("common.expand") : t("common.collapse")}
          style={{
            backgroundColor:
              colorScheme === "dark"
                ? "var(--se-background-alternative)"
                : "#ffffff",
            color: colorScheme === "dark" ? "#ffffff" : "#333333",
            border:
              colorScheme === "dark"
                ? "1px solid var(--se-accordian-standard)"
                : "1px solid #dee2e6",
            boxShadow:
              colorScheme === "dark"
                ? "0 2px 6px rgba(0,0,0,0.35)"
                : "0 2px 6px rgba(0,0,0,0.15)",
          }}
        >
          {collapseSideMenu.leftMenu ? (
            <IconChevronRight size={16} />
          ) : (
            <IconChevronLeft size={16} />
          )}
        </ActionIcon>
      </div>
      <div
        className="sidebar-content"
        style={{
          backgroundColor:
            colorScheme === "dark"
              ? "var(--se-background-alternative)"
              : "inherit",
        }}
        ref={ref}
        
      >
        {props.loading ? (
          <SideBarSkeleton />
        ) : (
          <Menu       >
            {projectDetails?.projectDTO?.projectName && (
              <MenuItem
                // data-intro="Click to Collapse or Expand Side Menu"
                // data-position="right"
                label={projectDetails?.projectDTO?.projectName}
                className={`${
                  colorScheme === "dark" ? "dark-menu" : ""
                } project-menu`}
                icon={
                  <se-icon
                    title={projectDetails?.projectDTO?.projectName}
                    size="medium"
                    onClick={() => {
                      setCollapseSideMenu((prev) => ({
                        ...prev,
                        leftMenu: !prev.leftMenu,
                      }));
                    }}
                  >
                    project
                  </se-icon>
                }
              >
                {/* ...existing code for project name and edit link... */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    lineHeight: "13px",
                  }}
                >
                  <span
                    title={projectDetails?.projectDTO?.projectName}
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      paddingTop: "5%",
                    }}
                  >
                    {projectDetails?.projectDTO?.projectName}
                  </span>
                  <se-link aria-label="edit-project-link">
                    <span
                      onClick={props.toggleEditDetails}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          props.toggleEditDetails?.(e);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      style={{ fontSize: "13px", cursor: "pointer" }}
                    >
                      {t("sideMenu.editProject")}
                    </span>
                  </se-link>
                </div>
                <se-divider className="side-menu-header-divider" />
              </MenuItem>
            )}
            {projectDetails?.designWithVersionsDTOList?.map(
              (designItem, index) => {
                const versions = designItem?.designVersionDTO || [];
                return (
                  <React.Fragment
                    key={`design-fragment-${designItem?.designId}-${index}`}
                    
                  >
                    <MenuItem
                      key={`design-${designItem?.designId}-${index}`}
               
                      className={`design-header-item ${
                        colorScheme === "dark" ? "dark-menu" : ""
                      }`}
                      icon={
                        <se-icon title={designItem?.designDTO?.designName}>
                          3d
                        </se-icon>
                      }
                    >
                      <div
                        className="design-label-ellipsis"
                        title={designItem?.designDTO?.designName}
                      >
                        {designItem?.designDTO?.designName}
                      </div>
                    </MenuItem>
                    {versions.map((ver, index) => {
                      const isActiveVersion =
                        activeTab === ver?.designVersionId.toString();
                      return (
                        <MenuItem
                          key={`version-${designItem?.designId}-${ver?.designId}-${index}`}
                          className={`version-item flat-version ${
                            isActiveVersion ? "version-active" : ""
                          }`}
                          style={{
                            backgroundColor: getMenuItemBackgroundColor(
                              ver?.designVersionId?.toString()
                            ),
                            position: "relative",
                            // borderBottom: getMenuItemBorderColor(
                            //   ver?.designVersionId?.toString()
                            // ),
                            // borderTop: getMenuItemBorderColor(
                            //   ver?.designVersionId?.toString()
                            // )
                          }}
                          active={isActiveVersion}
                          onClick={() =>
                            handleMenuItemClick(
                              ver?.designVersionId?.toString()
                            )
                          }
                          icon={
                            !collapseSideMenu.leftMenu ? (
                              <se-icon title={ver?.version} color="primary">
                                action_layer_backward
                              </se-icon>
                            ) : undefined
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: collapseSideMenu.leftMenu
                                ? "center"
                                : "space-between",
                              width: "100%",
                            }}
                          >
                            <div
                              className="version-label-ellipsis"
                              title={ver?.version}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                ...(collapseSideMenu.leftMenu
                                  ? { justifyContent: "center", textAlign: "center" }
                                  : { justifyContent: "space-between" }),
                              }}
                            >
                              <span
                                className="version-name"
                                style={{
                                  minWidth: 0,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  ...(collapseSideMenu.leftMenu ? {} : { flex: 1 }),
                                }}
                              >
                                {ver?.version}
                              </span>
                              {(ver?.versionStatus === "COMPLETED" || ver?.versionStatus === "SIMULATED") && !collapseSideMenu.leftMenu && (
                                <span
                                  aria-label="Restricted"
                                  title={ver?.versionStatus === "SIMULATED" ? "Simulation In-Progress" : "Editing restricted"}
                                  style={{ marginLeft: 6, fontSize: 14, flex: "0 0 auto" }}
                                >
                                  🔒
                                </span>
                              )}
                            </div>
                            {!collapseSideMenu.leftMenu &&
                              (userScope?.access?.createOrCopyVersion ||
                                userScope?.access?.deleteVersion) && (
                                <span
                                    data-testid={`version-context-menu-button-${ver?.designVersionId}`}
                                  ref={(el) => {
                                    if (el)
                                      contextMenuIconRefs.current[
                                        ver?.designVersionId
                                      ] = el;
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (openMenuVersion === ver?.designVersionId) {
                                        setOpenMenuVersion(null);
                                      } else {
                                        const el =
                                          contextMenuIconRefs.current[
                                            ver?.designVersionId
                                          ];
                                        if (el) {
                                          const rect = el.getBoundingClientRect();
                                          setMenuPosition({
                                            left: rect.right + 8,
                                            top: rect.bottom + 4,
                                          });
                                        }
                                        setOpenMenuVersion(ver?.designVersionId);
                                      }
                                    }
                                  }}
                                  style={{
                                    marginLeft: 8,
                                    cursor: "pointer",
                                    fontSize: 20,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 32,
                                    height: 32,
                                    borderRadius: 16,
                                    background: "rgba(0,0,0,0.04)",
                                    transition: "background 0.2s",
                                  }}
                                  className="context-menu-icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (openMenuVersion === ver?.designVersionId) {
                                      setOpenMenuVersion(null);
                                    } else {
                                      const el =
                                        contextMenuIconRefs.current[
                                          ver?.designVersionId
                                        ];
                                      if (el) {
                                        const rect = el.getBoundingClientRect();
                                        setMenuPosition({
                                          left: rect.right + 8,
                                          top: rect.bottom + 4,
                                        });
                                      }
                                      setOpenMenuVersion(ver?.designVersionId);
                                    }
                                  }}
                                  title={t("common.moreOptions")}
                                >
                                  &#8942;
                                </span>
                              )}
                          </div>
                          {/* Context menu dropdown using createPortal */}
                          {!collapseSideMenu.leftMenu &&
                            openMenuVersion === ver?.designVersionId &&
                            createPortal(
                              <div
                                ref={contextMenuPortalRef}
                                data-testid={`version-context-menu-${ver?.designVersionId}`}
                                style={{
                                  position: "fixed",
                                  left: menuPosition.left,
                                  top: menuPosition.top,
                                  background:
                                    colorScheme === "dark" ? "#23272e" : "#fff",
                                  border:
                                    colorScheme === "dark"
                                      ? "1px solid #444"
                                      : "1px solid #ccc",
                                  borderRadius: 4,
                                  zIndex: 9999,
                                  minWidth: 120,
                                  boxShadow:
                                    colorScheme === "dark"
                                      ? "0 2px 8px rgba(0,0,0,0.35)"
                                      : "0 2px 8px rgba(0,0,0,0.15)",
                                  padding: "8px 0",
                                  color:
                                    colorScheme === "dark" ? "#fff" : "#222",
                                  opacity:
                                    openMenuVersion === ver?.designVersionId
                                      ? 1
                                      : 0,
                                  transform:
                                    openMenuVersion === ver?.designVersionId
                                      ? "translateY(0)"
                                      : "translateY(-10px)",
                                  transition: "opacity 0.2s, transform 0.2s",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "4px 16px",
                                    cursor: "pointer",
                                    gap: 8,
                                    transition: "background 0.2s",
                                  }}
                                  className="menu-option"
                                  role="button"
                                  tabIndex={0}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.background =
                                      colorScheme === "dark"
                                        ? "#2c313a"
                                        : "#f5f5f5")
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.background =
                                      "inherit")
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setVersionDetailsData(ver);
                                      setVersionDetailsDesignId(
                                        designItem?.designDTO?.designId ??
                                          designItem?.designId ??
                                          null
                                      );
                                      setShowVersionDetailsModal(true);
                                      queueMicrotask(() => setOpenMenuVersion(null));
                                    }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setVersionDetailsData(ver);
                                    setVersionDetailsDesignId(
                                      designItem?.designDTO?.designId ??
                                        designItem?.designId ??
                                        null
                                    );
                                    setShowVersionDetailsModal(true);
                                    queueMicrotask(() => setOpenMenuVersion(null));
                                  }}
                                >
                                  <se-icon size="nano">information_stroke</se-icon>
                                  Details
                                </div>
                                {userScope?.access?.createOrCopyVersion && (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      padding: "4px 16px",
                                      cursor: "pointer",
                                      gap: 8,
                                      transition: "background 0.2s",
                                    }}
                                    className="menu-option"
                                    role="button"
                                    tabIndex={0}
                                    onMouseEnter={(e) =>
                                      (e.currentTarget.style.background =
                                        colorScheme === "dark"
                                          ? "#2c313a"
                                          : "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.currentTarget.style.background =
                                        "inherit")
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const design =
                                          projectDetails?.designWithVersionsDTOList?.find(
                                            (d) =>
                                              d?.designDTO?.designId ===
                                              Number(designId)
                                          );
                                        const versionsArr =
                                          design?.designVersionDTO || [];
                                        if (versionsArr.length >= 9) {
                                          setOpenMenuVersion(null);
                                          setShowMaxVersionModal(true);
                                          return;
                                        }
                                        setVersionToDuplicate(ver);
                                        cloneForm.reset(
                                          ver?.version ? `${ver.version} Copy` : "",
                                          ver?.versionDescription || ver?.description || ""
                                        );
                                        setShowDuplicateVersionModal(true);
                                        queueMicrotask(() => setOpenMenuVersion(null));
                                      }
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Check max version count before allowing duplicate
                                      const design =
                                        projectDetails?.designWithVersionsDTOList?.find(
                                          (d) =>
                                            d?.designDTO?.designId ===
                                            Number(designId)
                                        );
                                      const versionsArr =
                                        design?.designVersionDTO || [];
                                      if (versionsArr.length >= 9) {
                                        setOpenMenuVersion(null); // Close context menu
                                        setShowMaxVersionModal(true);
                                        return;
                                      }
                                      setVersionToDuplicate(ver);
                                      cloneForm.reset(
                                        ver?.version ? `${ver.version} Copy` : "",
                                        ver?.versionDescription || ver?.description || ""
                                      );
                                      setShowDuplicateVersionModal(true);
                                      // Close context menu after state is set (avoids any
                                      // ordering issues with global mousedown handlers).
                                      queueMicrotask(() => setOpenMenuVersion(null));
                                    }}
                                  >
                                    <se-icon size="nano">
                                      action_duplicate
                                    </se-icon>
                                    {t("sideMenu.duplicateVersion")}
                                    {/* <IconCopy size={18} /> Duplicate */}
                                  </div>
                                )}

                                {userScope?.access?.deleteVersion && (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      padding: "4px 16px",
                                      cursor:
                                        ver?.versionStatus === "SIMULATED"
                                          ? "not-allowed"
                                          : "pointer",
                                      gap: 8,
                                      transition: "background 0.2s",
                                      pointerEvents:
                                        ver?.versionStatus === "SIMULATED"
                                          ? "none"
                                          : "auto",
                                      opacity:
                                        ver?.versionStatus === "SIMULATED"
                                          ? 0.5
                                          : 1,
                                    }}
                                    className="menu-option"
                                    role="button"
                                    tabIndex={ver?.versionStatus === "SIMULATED" ? -1 : 0}
                                    onMouseEnter={(e) =>
                                      ver?.versionStatus === "SIMULATED"
                                        ? null
                                        : (e.currentTarget.style.background =
                                            colorScheme === "dark"
                                              ? "#2c313a"
                                              : "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                      ver?.versionStatus === "SIMULATED"
                                        ? null
                                        : (e.currentTarget.style.background =
                                            "inherit")
                                    }
                                    onKeyDown={(e) => {
                                      if (ver?.versionStatus === "SIMULATED") return;
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setOpenMenuVersion(null);
                                        setVersionToDelete(ver);
                                        setShowDeleteVersionModal(true);
                                      }
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // if (ver?.versionStatus === 'SIMULATED') return; // Disabled when simulated
                                      setOpenMenuVersion(null); // Close context menu
                                      setVersionToDelete(ver);
                                      setShowDeleteVersionModal(true);
                                    }}
                                  >
                                    <se-icon
                                      size="nano"
                                      style={{
                                        opacity:
                                          ver?.versionStatus === "SIMULATED"
                                            ? 0.5
                                            : 1,
                                      }}
                                    >
                                      action_delete
                                    </se-icon>
                                    {t("common.delete")}
                                    {/* <IconTrash size={18} /> Delete */}
                                  </div>
                                )}
                              </div>,
                              document.body
                            )}
                        </MenuItem>
                      );
                    })}
                    {/* Add Version button/icon at the end of all versions */}
                    {collapseSideMenu.leftMenu
                      ? userScope?.access?.createOrCopyVersion && (
                          <a
                            key={`add-version-${designItem?.designId}-${index}`}
                            className={`add-version-item ${
                              colorScheme === "dark" ? "dark-menu" : ""
                            }`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "10px 0",
                              cursor: "pointer",
                              color: "#42b4e6",
                              fontSize: "22px",
                              textDecoration: "none",
                              marginBottom: "4px",
                              width: "100%",
                              borderLeft: "5px solid transparent",
                            }}
                            title={t("sideMenu.addVersion")}
                            onClick={(e) => {
                              e.preventDefault();
                              const versionsArr =
                                designItem?.designVersionDTO || [];
                              if (versionsArr.length >= 9) {
                                setShowMaxVersionModal(true);
                                return;
                              }
                              setModalDesignName(
                                designItem?.designDTO?.designName || ""
                              );
                              setModalProjectName(
                                projectDetails?.projectDTO?.projectName || ""
                              );
                                      createForm.reset();
                              setShowAddVersionModal(true);
                            }}
                            href="#"
                          >
                            <se-icon
                              style={{ color: "#3dcd58", cursor: "pointer" }}
                              size="small"
                            >
                              action_add_flat
                            </se-icon>
                          </a>
                        )
                      : userScope?.access?.createOrCopyVersion && (
                          <a
                            key={`add-version-${designItem?.designId}-${index}`}
                            className={`add-version-item ${
                              colorScheme === "dark" ? "dark-menu" : ""
                            }`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "14px 16px",
                              cursor: "pointer",
                              color: "#42b4e6",
                              fontWeight: 600,
                              fontSize: "15px",
                              textDecoration: "none",
                              marginBottom: "4px",
                              justifyContent: "center",
                              width: "100%",
                            }}
                            title={t("sideMenu.addVersion")}
                            onClick={(e) => {
                              e.preventDefault();
                              const versionsArr =
                                designItem?.designVersionDTO || [];
                              if (versionsArr.length >= 9) {
                                setShowMaxVersionModal(true);
                                return;
                              }
                              setModalDesignName(
                                designItem?.designDTO?.designName || ""
                              );
                              setModalProjectName(
                                projectDetails?.projectDTO?.projectName || ""
                              );
                                      createForm.reset();
                              setShowAddVersionModal(true);
                            }}
                            href="#"
                          >
                            <span
                              className="add-version-label-ellipsis"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                whiteSpace: "nowrap",
                                minWidth: 0,
                                gap: 6,
                              }}
                            >
                              <se-icon
                                style={{
                                  color: "#3dcd58",
                                  cursor: "pointer",
                                  flex: "0 0 auto",
                                }}
                                size="small"
                              >
                                action_add_flat
                              </se-icon>
                              <span
                                style={{
                                  flex: "1 1 auto",
                                  minWidth: 0,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {t("sideMenu.addVersion")}
                              </span>
                            </span>
                          </a>
                        )}
                  </React.Fragment>
                );
              }
            )}
          </Menu>
        )}
      </div>
      {/* Collaborators bar (only if shared) */}
      {projectDetails?.projectDTO?.isShared && (
        <div
          onClick={openCollaborators}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openCollaborators();
            }
          }}
          role="button"
          tabIndex={0}
          className="sidebar-collaborators d-flex justify-content-center"
          style={{
            height:  "48px",
            alignItems: "center",
            cursor: "pointer",
            backgroundColor:
              colorScheme === "dark"
                ? "var(--se-background-alternative)"
                : "inherit",
            borderTop:
              colorScheme === "dark"
                ? "1px solid var(--se-accordian-standard)"
                : "1px solid #dee2e6",
          }}
          title={t("sideMenu.collaborators")}
        >
          {collapseSideMenu.leftMenu ? (
            <se-icon>user_group</se-icon>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <se-icon>user_group</se-icon>
              <span style={{ fontSize: "14px", fontWeight: 500 }}>
                {t("sideMenu.collaborators")}
              </span>
            </div>
          )}
        </div>
      )}
      <SEConfirmModal
        opened={showVersionDetailsModal}
        onClose={() => {
          setShowVersionDetailsModal(false);
          setVersionDetailsData(null);
          setVersionDetailsDesignId(null);
        }}
        header={t("sideMenu.versionDetails.title")}
        confirmLabel={t("common.save")}
        cancelLabel={t("common.cancel")}
        confirmLoading={loader}
        confirmLoadingLabel={t("sideMenu.versionDetails.saving")}
        onConfirm={async () => {
          setVersionDetailsNameError("");
          setVersionDetailsDescError("");

          const nameTrim = versionDetailsName.trim();
          const descTrim = versionDetailsDesc.trim();
           const hasHtml = (value) => isHtmlTag(value);
          let hasError = false;
          const targetDesignId =
            versionDetailsDesignId ?? designId ?? versionDetailsData?.designId;

          if (!nameTrim) {
            setVersionDetailsNameError(t("sideMenu.validation.nameRequired"));
            hasError = true;
          }
           if (!hasError && hasHtml(nameTrim)) {
             setVersionDetailsNameError("HTML tags are not allowed");
             hasError = true;
           }
          if (nameTrim.length > 15) {
            setVersionDetailsNameError(t("sideMenu.validation.nameMax"));
            hasError = true;
          }
          if (!descTrim) {
            setVersionDetailsDescError(t("sideMenu.validation.descRequired"));
            hasError = true;
          }
           if (!hasError && hasHtml(descTrim)) {
             setVersionDetailsDescError("HTML tags are not allowed");
             hasError = true;
           }
          if (descTrim.length > 60) {
            setVersionDetailsDescError(t("sideMenu.validation.descMax"));
            hasError = true;
          }

          if (!hasError) {
            const currentDesign = projectDetails?.designWithVersionsDTOList?.find(
              (d) => String(d?.designDTO?.designId) === String(targetDesignId)
            );
            const versionsArr = currentDesign?.designVersionDTO || [];
            const normalizedName = nameTrim.toLowerCase();
            const isDuplicate = versionsArr.some(
              (v) =>
                String(v?.designVersionId) !==
                  String(versionDetailsData?.designVersionId) &&
                (v?.version || "").trim().toLowerCase() === normalizedName
            );
            if (isDuplicate) {
              setVersionDetailsNameError(t("sideMenu.validation.nameUnique"));
              hasError = true;
            }
          }

          if (hasError) return;
          setLoader(true);
          try {
            await MGA_API.createDesignVersion(
              projectId,
              targetDesignId,
              versionDetailsData?.designVersionId,
              {
                version: nameTrim,
                versionDescription: descTrim,
                isUpdate: true,
              }
            );

            // Refresh project data to keep local state in sync with server
            const editRes = await MGA_API.editProject(projectId);
            const refreshedDesigns = applyProjectRefresh(editRes);
            const updatedDesign = refreshedDesigns.find(
              (d) => String(d?.designDTO?.designId) === String(targetDesignId)
            );
            const updatedVersion = updatedDesign?.designVersionDTO?.find(
              (v) =>
                String(v?.designVersionId) ===
                String(versionDetailsData?.designVersionId)
            );

            const nextName = updatedVersion?.version || nameTrim;
            const nextDesc =
              updatedVersion?.versionDescription ||
              updatedVersion?.description ||
              descTrim;

            setVersionDetailsName(nextName);
            setVersionDetailsDesc(nextDesc);
            setVersionDetailsData((prev) =>
              updatedVersion
                ? { ...prev, ...updatedVersion }
                : prev
            );
            setShowVersionDetailsModal(false);
          } catch (err) {
            displayError('error', getErrorMessage(err), t("sideMenu.versionDetails.saveFailed"));
          } finally {
            setLoader(false);
          }
        }}
        message={
          <Stack spacing="sm" sx={{ minWidth: "260px" }}>
            <TextInput
              label={t("sideMenu.versionDetails.nameLabel")}
              value={versionDetailsName}
              onChange={(e) => {
                setVersionDetailsName(e.target.value);
                setVersionDetailsNameError("");
              }}
              required
              error={versionDetailsNameError || undefined}
            />
            <Textarea
              label={t("sideMenu.versionDetails.descLabel")}
              minRows={2}
              value={versionDetailsDesc}
              onChange={(e) => {
                setVersionDetailsDesc(e.target.value);
                setVersionDetailsDescError("");
              }}
              required
              error={versionDetailsDescError || undefined}
            />
            {versionDetailsData?.updatedOn && (
              <TextInput
                label={t("sideMenu.versionDetails.updatedOn")}
                value={formatUpdatedOn(versionDetailsData.updatedOn)}
                disabled
                readOnly
              />
            )}
          </Stack>
        }
      />
      {/* Collaborators SEConfirmModal */}
      <SEConfirmModal
        opened={collabModalOpen}
        onClose={() => {setCollabModalOpen(false)
            // setCollapseSideMenu((prev) => ({ ...prev, leftMenu: true }))

        }}
         header={t("sideMenu.collaborators")}
        confirmLabel={t("common.ok")}
        onlyOk
        message={
          <div
            style={{ maxHeight: "50vh", overflow: "auto", minWidth: "300px" }}
          >
            {collabLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                }}
              >
                <Loader size="sm" />
              </div>
            ) : collabError ? (
              <div style={{ color: "red", fontSize: 14 }}>{collabError}</div>
            ) : (
              <Table striped highlightOnHover withTableBorder withColumnBorders>
                <thead>
                  <tr>
                    <th style={{ width: "60px" }}>{t("sideMenu.collaboratorsTable.index")}</th>
                    <th>{t("profile.label.email")}</th>
                  </tr>
                </thead>
                <tbody>
                  {collaborators.length === 0 && (
                    <tr>
                      <td
                        colSpan={2}
                        style={{
                          textAlign: "center",
                          fontStyle: "italic",
                          color: "#666",
                        }}
                      >
                        {t("sideMenu.noCollaborators")}
                      </td>
                    </tr>
                  )}
                  {collaborators.map((c, idx) => (
                    <tr
                      key={idx}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSharedUserClick(c.email)}
                    >
                      <td>{idx + 1}</td>
                      <td style={{ wordBreak: "break-all" }}>{c.email}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        }
      />
      <SEConfirmModal
        opened={showClonedVersionModal}
        onClose={() => {
          setShowClonedVersionModal(false);
          collapseLeftMenu();
        }}
        onlyOk
        header={t("sideMenu.versionCloned")}
        message={
          <Trans
            i18nKey="sideMenu.versionClonedMessage"
            values={{ name: clonedVersionName }}
            components={{ b: <b /> }}
          />
        }
        confirmLabel={t("common.ok")}
      />
      <SEConfirmModal
        opened={sharedUserProfileModalOpen}
        onClose={() => {
          setSharedUserProfileModalOpen(false);
          setSharedUserProfileData(null);
          collapseLeftMenu();
        }}
        onlyOk
        confirmLabel={t("common.ok")}
        header={t("sideMenu.userInfo")}
        message={
          sharedUserProfileLoading ? (
            <Group position="center" p="md">
              <Loader size="sm" />
            </Group>
          ) : sharedUserProfileError ? (
            <Text size="sm" color="red">
              {sharedUserProfileError}
            </Text>
          ) : sharedUserProfileData ? (
            (() => {
              const statusText =
                sharedUserProfileData.status === "A" ? t("profile.status.active") : t("profile.status.inactive");
              let updatedDisplay = sharedUserProfileData.updated_at || "—";
              const match =
                /^(\d{4})\/(\d{2})\/(\d{2}) (\d{2}:\d{2}:\d{2})$/.exec(
                  updatedDisplay
                );
              if (match) {
                const [, y, m, d, t] = match;
                const dateObj = new Date(`${y}-${m}-${d}T${t}Z`);
                if (!isNaN(dateObj.getTime())) {
                  const opts = {
                    month: "short",
                    day: "2-digit",
                    year: "numeric",
                  };
                  const datePart = dateObj.toLocaleDateString(undefined, opts);
                  updatedDisplay = `${datePart} ${t}`;
                }
              }
              console.log({sharedUserProfileData})
              return (
                <Grid gutter="md" align="flex-start" sx={{ minWidth: "360px" }}>
                  <Col span={8}>
                    <Stack spacing="sm">
                      <div>
                        <Text
                          size="xs"
                          transform="uppercase"
                          weight={600}
                          color="dimmed"
                        >
                          Name
                        </Text>
                        <Text weight={600}>
                          {sharedUserProfileData?.firstName+" "+sharedUserProfileData?.lastName || "—"}
                        </Text>
                      </div>
                      <div>
                        <Text
                          size="xs"
                          transform="uppercase"
                          weight={600}
                          color="dimmed"
                        >
                          Email
                        </Text>
                        <Text size="sm" sx={{ wordBreak: "break-all" }}>
                          {sharedUserProfileData.email || "—"}
                        </Text>
                      </div>
                    </Stack>
                  </Col>
                  <Col span={4}>
                    <Group position="center" sx={{ height: "100%" }}>
                      {sharedUserProfileData?.imageLink ? (
                        <Avatar
                          src={convertPictureToBlobUrl(sharedUserProfileData?.imageLink)}
                          size={90}
                          radius="md"
                          alt={sharedUserProfileData?.firstName+" "+sharedUserProfileData?.lastName || "Profile"}
                        />
                      ) : (
                        <Avatar size={90} radius="md" color="gray">
                          {sharedUserProfileData?.firstName+" "+sharedUserProfileData?.lastName || "?"}
                        </Avatar>
                      )}
                    </Group>
                  </Col>
                </Grid>
              );
            })()
          ) : (
            <Text size="sm" color="dimmed">
              No data available.
            </Text>
          )
        }
      />
      <SEConfirmModal
        opened={showAddVersionModal}
        onClose={() => {
          setShowAddVersionModal(false);
          collapseLeftMenu();
        }}
        confirmLoading={loader}
        confirmLoadingLabel="Creating..."
        onConfirm={async () => {
          const currentDesign = projectDetails?.designWithVersionsDTOList?.find(
            (d) => String(d?.designDTO?.designId) === String(designId)
          );
          const validation = validateVersionInputs({
            name: createForm.values.name,
            desc: createForm.values.desc,
            versions: currentDesign?.designVersionDTO,
          });

          createForm.setErrors({ name: validation.nameError, desc: validation.descError });
          if (validation.nameError || validation.descError) return;

          setLoader(true);
          try {
            const { res, editRes } = await runVersionMutation({
              action: "create",
              name: validation.trimmedName,
              desc: validation.trimmedDesc,
            });

            const newVersionId = res?.designVersionId;
            const design = editRes?.designWithVersionsDTOList?.find(
              (d) => d?.designDTO?.designId === Number(designId)
            );
            const versionsArr = design?.designVersionDTO || [];
            const createdVersion = newVersionId
              ? versionsArr?.find((v) => v?.designVersionId === newVersionId)
              : versionsArr?.find(
                  (v) => (v?.version || "").trim() === validation.trimmedName
                );
            if (createdVersion) {
              setCreatedVersionName(createdVersion?.version || "");
              setShowCreatedVersionModal(true);
              setActiveState("DESIGN");
            }
            setShowAddVersionModal(false);
              createForm.reset();
          } catch (err) {
            displayError('error', getErrorMessage(err));
          } finally {
            setLoader(false);
            collapseLeftMenu();
          }
        }}
        header={t("sideMenu.createVersion.title")}
        message={
          <VersionFormFields
            heading={
              <Text size="sm">
                {t("sideMenu.createVersion.confirm", { design: modalDesignName, project: modalProjectName })}
              </Text>
            }
            nameLabel="Version name"
            descLabel="Version description"
            nameValue={createForm.values.name}
            onNameChange={(e) => createForm.setName(e.target.value)}
            descValue={createForm.values.desc}
            onDescChange={(e) => createForm.setDesc(e.target.value)}
            nameError={createForm.errors.name}
            descError={createForm.errors.desc}
          />
        }
        confirmLabel={t("common.ok")}
        cancelLabel={t("common.cancel")}
      />
      <SEConfirmModal
        opened={showCreatedVersionModal}
        onClose={() => {
          setShowCreatedVersionModal(false);
          setCreatedVersionName("");
          collapseLeftMenu();
        }}
        onlyOk
        header={t("sideMenu.versionCreated.title")}
        message={
          <Trans
            i18nKey="sideMenu.versionCreated.message"
            values={{ name: createdVersionName }}
            components={{ b: <b /> }}
          />
        }
        confirmLabel={t("common.ok")}
      />

      <SEConfirmModal
        opened={showVersionSwitchBlockedModal}
        onClose={() => {
          setShowVersionSwitchBlockedModal(false);
          setBlockedComponentName("");
          if (pendingVersionId) {
            navigateToVersion(pendingVersionId);
          }
          setPendingVersionId(null);
          collapseLeftMenu();
        }}
        onConfirm={() => {
          setShowVersionSwitchBlockedModal(false);
          setBlockedComponentName("");
          setPendingVersionId(null);
          collapseLeftMenu();
        }}
        header={"Warning"}
        message={
          <>
            {t("sideMenu.versionSwitchBlocked.message", {
              component: blockedComponentName?.toLocaleUpperCase() || t("common.selected"),
            })}
          </>
        }
        confirmLabel={t("common.ok")}
        cancelLabel={t("common.cancel")}
      />

      <SEConfirmModal
        opened={showMaxVersionModal}
        onClose={() => {
          setShowMaxVersionModal(false);
          collapseLeftMenu();
        }}
        onlyOk
        header={t("sideMenu.maxVersions.title")}
        message={<>{t("sideMenu.maxVersions.message")}</>}
        confirmLabel={t("common.ok")}
      />

      <SEConfirmModal
        opened={showDeleteVersionModal}
        onClose={() => {
          setShowDeleteVersionModal(false);
          setVersionToDelete(null);
          collapseLeftMenu();
        }}
        confirmLoading={loader}
        confirmLoadingLabel="Deleting..."
        onConfirm={async () => {
          setShowDeleteVersionModal(false);
          if (versionToDelete) {
            await handleDeleteVersion(versionToDelete);
            setVersionToDelete(null);
          }
          collapseLeftMenu();
        }}
        header={t("sideMenu.deleteVersion.title")}
        message={
          <>
            {t("sideMenu.deleteVersion.confirm", { version: versionToDelete?.version })}
          </>
        }
        confirmLabel={t("common.ok")}
        cancelLabel={t("common.cancel")}
      />
      <SEConfirmModal
        opened={showDuplicateVersionModal}
        onClose={() => {
          setShowDuplicateVersionModal(false);
          setVersionToDuplicate(null);
          collapseLeftMenu();
        }}
        confirmLoading={loader}
        confirmLoadingLabel="Cloning..."
        onConfirm={async () => {
          if (versionToDuplicate) {
            const currentDesign = projectDetails?.designWithVersionsDTOList?.find(
              (d) => String(d?.designDTO?.designId) === String(designId)
            );
            const validation = validateVersionInputs({
              name: cloneForm.values.name,
              desc: cloneForm.values.desc,
              versions: currentDesign?.designVersionDTO,
            });

            cloneForm.setErrors({ name: validation.nameError, desc: validation.descError });

            if (validation.nameError || validation.descError) return;

            setLoader(true);
            try {
              const { res, editRes } = await runVersionMutation({
                action: "clone",
                name: validation.trimmedName,
                desc: validation.trimmedDesc,
                sourceVersionId: versionToDuplicate?.designVersionId,
              });

              setClonedVersionName(
                res?.version || validation.trimmedName || ""
              );
              const design = editRes?.designWithVersionsDTOList?.find(
                (d) => d?.designDTO?.designId === Number(designId)
              );
              const versionsArr = design?.designVersionDTO || [];
              const clonedVersion = res?.designVersionId
                ? versionsArr?.find(
                    (v) => v?.designVersionId === res?.designVersionId
                  )
                : versionsArr?.find(
                    (v) => (v?.version || "").trim() === validation.trimmedName
                  );
              if (clonedVersion) {
                setActiveState("DESIGN");
              }
              setShowClonedVersionModal(true);
              setShowDuplicateVersionModal(false);
              setVersionToDuplicate(null);
              cloneForm.reset();
            } catch (err) {
                displayError('error', getErrorMessage(err), "Failed to clone version");
            } finally {
              setLoader(false);
            }
          }
          collapseLeftMenu();
        }}
        header={t("sideMenu.cloneVersion.title")}
        message={
          <VersionFormFields
            heading={
              <Text size="sm">
                {t("sideMenu.cloneVersion.confirm", { version: versionToDuplicate?.version })}
              </Text>
            }
            nameLabel="New version name"
            descLabel="New version description"
            nameValue={cloneForm.values.name}
            onNameChange={(e) => cloneForm.setName(e.target.value)}
            descValue={cloneForm.values.desc}
            onDescChange={(e) => cloneForm.setDesc(e.target.value)}
            nameError={cloneForm.errors.name}
            descError={cloneForm.errors.desc}
          />
        }
        confirmLabel={t("common.ok")}
        cancelLabel={t("common.cancel")}
      />
    </Sidebar>
  );
}

SideMenu.propTypes = {
  loading: PropTypes.bool,
  projectDetails: PropTypes.object,
  toggleEditDetails: PropTypes.func,
  handleSideMenuChange: PropTypes.func,
};